import { useEffect, useState } from 'react';
import apiClient from '@/lib/api/client';

// ── Live change-request work stream (#69) ───────────────────────────────────
// Unifies the Task Queue with the change-request framework: the actual work
// Claude does — filed, in progress, fulfilled with changelogs — shown live
// beside the legacy dev-task backlog.

interface CR {
  id: number; title: string; page: string; status: string; priority: string;
  source: string; fulfill_now: number; changelog?: string | null;
  details?: string | null;
  created_at: string; fulfilled_at?: string | null;
}

const STATUS_ORDER = ['in_progress', 'open', 'proposed', 'done', 'rejected'];
const STATUS_META: Record<string, { label: string; cls: string; icon: string }> = {
  in_progress: { label: 'In progress', cls: 'bg-blue-100 text-blue-700', icon: 'progress_activity' },
  open:        { label: 'Queued',      cls: 'bg-amber-100 text-amber-800', icon: 'schedule' },
  proposed:    { label: 'Proposed',    cls: 'bg-purple-100 text-purple-700', icon: 'lightbulb' },
  done:        { label: 'Done',        cls: 'bg-green-100 text-green-700', icon: 'check_circle' },
  rejected:    { label: 'Rejected',    cls: 'bg-gray-100 text-gray-500', icon: 'cancel' },
};

export function ChangeRequestStream() {
  const [reqs, setReqs] = useState<CR[]>([]);
  const [show, setShow] = useState<'active' | 'all' | 'done'>('active');
  const [q, setQ] = useState(''); // CR #150 — search the whole history
  // CR #173 — spreadsheet view with inline CRUD
  const [sheet, setSheet] = useState(false);
  const [expandedRow, setExpandedRow] = useState<number | null>(null);
  const [newRow, setNewRow] = useState<{ page: string; title: string; details: string } | null>(null);
  const patchCell = async (id: number, field: string, value: unknown) => {
    setReqs(rs => rs.map(r => r.id === id ? { ...r, [field]: value } as CR : r));
    try { await apiClient.put(`/dev/change-requests/${id}`, { [field]: value }); } catch { load(); }
  };
  const deleteRow = async (id: number) => {
    if (!window.confirm(`Delete queue entry #${id}? This removes its changelog history too.`)) return;
    setReqs(rs => rs.filter(r => r.id !== id));
    try { await apiClient.delete(`/dev/change-requests/${id}`); } catch { load(); }
  };
  const addRow = async () => {
    if (!newRow?.title.trim()) return;
    const form = new FormData();
    form.append('page', newRow.page.trim() || 'General');
    form.append('title', newRow.title.trim());
    form.append('details', newRow.details.trim());
    form.append('priority', 'normal');
    try {
      await apiClient.post('/dev/change-requests', form, { headers: { 'Content-Type': 'multipart/form-data' } });
      setNewRow(null);
      load();
    } catch { /* keep the draft row so nothing is lost */ }
  };
  const [openId, setOpenId] = useState<number | null>(null);

  const load = () => apiClient.get('/dev/change-requests').then(r => setReqs(r.data.data ?? [])).catch(() => {});
  useEffect(() => { load(); const id = setInterval(load, 20000); return () => clearInterval(id); }, []);

  const today = new Date().toISOString().slice(0, 10);
  const doneToday = reqs.filter(r => r.status === 'done' && (r.fulfilled_at || '').slice(0, 10) === today).length;
  const active = reqs.filter(r => ['in_progress', 'open', 'proposed'].includes(r.status));

  // CR #150: browse the FULL history — active (future work), completed, or all,
  // searchable across title/page/details/changelog.
  const needle = q.trim().toLowerCase();
  const matches = (r: CR) => !needle ||
    [r.title, r.page, (r as CR & { details?: string }).details, r.changelog]
      .some(v => String(v || '').toLowerCase().includes(needle));
  const visible = (show === 'active'
    ? reqs.filter(r => ['in_progress', 'open', 'proposed'].includes(r.status))
    : show === 'done'
      ? reqs.filter(r => r.status === 'done')
      : reqs).filter(matches);
  const doneCount = reqs.filter(r => r.status === 'done').length;
  const grouped = STATUS_ORDER
    .map(st => ({ st, rows: visible.filter(r => r.status === st) }))
    .filter(g => g.rows.length);

  const approve = async (id: number) => {
    await apiClient.put(`/dev/change-requests/${id}`, { status: 'open' }).catch(() => {});
    load();
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4 mb-4">
      <div className="flex items-center justify-between mb-2">
        <div>
          <h3 className="text-sm font-bold text-gray-900 flex items-center gap-1.5">
            <span className="material-symbols-outlined text-[#C77D3F] text-lg">bolt</span>
            Change-request work stream
          </h3>
          <p className="text-[11px] text-gray-400">
            {active.length} active · {doneToday} fulfilled today · {reqs.length} lifetime — the live queue Claude works
          </p>
        </div>
        <div className="flex gap-1 items-center flex-wrap justify-end">
          {/* CR #150 — search the full history, and jump to completed work */}
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search history…"
            className="h-7 px-2 rounded-lg border border-gray-200 text-[11px] w-40 focus:outline-none focus:border-[#C77D3F]" />
          <button onClick={() => setSheet(v => !v)}
            title="Spreadsheet view — inline edit, add and delete queue entries"
            className={`h-7 px-2.5 rounded-full text-[11px] font-semibold border flex items-center gap-1 ${sheet ? 'bg-[#C77D3F] text-white border-[#C77D3F]' : 'border-gray-200 text-gray-500'}`}>
            <span className="material-symbols-outlined text-sm">table</span>Sheet
          </button>
          {(['active', 'done', 'all'] as const).map(m => (
            <button key={m} onClick={() => setShow(m)}
              className={`h-7 px-2.5 rounded-full text-[11px] font-semibold border ${show === m ? 'bg-[#2C1810] text-white border-[#2C1810]' : 'border-gray-200 text-gray-500'}`}>
              {m === 'active' ? `Upcoming (${active.length})` : m === 'done' ? `Completed (${doneCount})` : 'All'}
            </button>
          ))}
        </div>
      </div>

      {sheet ? (
        /* CR #173 — Excel-style grid over the same filtered rows: every cell
           that makes sense to change edits inline and saves on blur/change. */
        <div className="overflow-x-auto border border-gray-200 rounded-lg">
          <table className="w-full text-xs min-w-[1080px] border-collapse">
            <thead>
              <tr className="bg-gray-50 text-left text-[10px] uppercase tracking-wide text-gray-500">
                {['#', 'Page', 'Title', 'Status', 'Priority', 'Now', 'Source', 'Created', 'Fulfilled', ''].map(h => (
                  <th key={h} className="border border-gray-200 px-2 py-1.5 font-semibold whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              <tr className="bg-[#FAF6F0]/60">
                <td className="border border-gray-200 px-2 py-1 text-gray-400">new</td>
                <td className="border border-gray-200 p-0">
                  <input value={newRow?.page ?? ''} placeholder="Page"
                    onChange={e => setNewRow(r => ({ page: e.target.value, title: r?.title ?? '', details: r?.details ?? '' }))}
                    className="w-full px-2 py-1 bg-transparent focus:outline-none focus:bg-white" />
                </td>
                <td className="border border-gray-200 p-0" colSpan={7}>
                  <input value={newRow?.title ?? ''} placeholder="New queue entry — type a title and press Add"
                    onChange={e => setNewRow(r => ({ page: r?.page ?? '', title: e.target.value, details: r?.details ?? '' }))}
                    onKeyDown={e => { if (e.key === 'Enter') addRow(); }}
                    className="w-full px-2 py-1 bg-transparent focus:outline-none focus:bg-white" />
                </td>
                <td className="border border-gray-200 px-1 py-1">
                  <button onClick={addRow} disabled={!newRow?.title.trim()}
                    className="h-6 px-2 rounded bg-[#C77D3F] text-white text-[10px] font-bold disabled:opacity-30">Add</button>
                </td>
              </tr>
              {visible.map(r => (
                <>
                  <tr key={r.id} className="hover:bg-gray-50">
                    <td className="border border-gray-200 px-2 py-1 font-mono text-gray-400 whitespace-nowrap cursor-pointer"
                      title="Show details & changelog"
                      onClick={() => setExpandedRow(x => x === r.id ? null : r.id)}>
                      {r.id}
                    </td>
                    <td className="border border-gray-200 p-0">
                      <input defaultValue={r.page}
                        onBlur={e => e.target.value !== r.page && patchCell(r.id, 'page', e.target.value)}
                        className="w-full max-w-[140px] px-2 py-1 bg-transparent focus:outline-none focus:bg-white truncate" />
                    </td>
                    <td className="border border-gray-200 p-0">
                      <input defaultValue={r.title}
                        onBlur={e => e.target.value !== r.title && patchCell(r.id, 'title', e.target.value)}
                        className="w-full min-w-[220px] px-2 py-1 bg-transparent focus:outline-none focus:bg-white" />
                    </td>
                    <td className="border border-gray-200 p-0">
                      <select value={r.status} onChange={e => patchCell(r.id, 'status', e.target.value)}
                        className={`w-full px-1 py-1 text-[11px] font-semibold bg-transparent focus:outline-none cursor-pointer ${STATUS_META[r.status]?.cls.replace('bg-', 'text-').split(' ')[1] || ''}`}>
                        {STATUS_ORDER.map(st => <option key={st} value={st}>{STATUS_META[st]?.label || st}</option>)}
                      </select>
                    </td>
                    <td className="border border-gray-200 p-0">
                      <select value={r.priority} onChange={e => patchCell(r.id, 'priority', e.target.value)}
                        className="w-full px-1 py-1 bg-transparent focus:outline-none cursor-pointer">
                        {['high', 'normal', 'low'].map(x => <option key={x} value={x}>{x}</option>)}
                      </select>
                    </td>
                    <td className="border border-gray-200 px-2 py-1 text-center">
                      <input type="checkbox" checked={!!r.fulfill_now}
                        onChange={e => patchCell(r.id, 'fulfill_now', e.target.checked)}
                        className="accent-[#C77D3F]" title="Fulfill now" />
                    </td>
                    <td className="border border-gray-200 px-2 py-1 text-gray-400">{r.source}</td>
                    <td className="border border-gray-200 px-2 py-1 text-gray-400 whitespace-nowrap">{(r.created_at || '').slice(0, 10)}</td>
                    <td className="border border-gray-200 px-2 py-1 text-gray-400 whitespace-nowrap">{(r.fulfilled_at || '').slice(0, 10) || '—'}</td>
                    <td className="border border-gray-200 px-1 py-1">
                      <button onClick={() => deleteRow(r.id)} title="Delete entry"
                        className="h-6 w-6 rounded text-gray-300 hover:text-red-600 hover:bg-red-50">
                        <span className="material-symbols-outlined text-sm">delete</span>
                      </button>
                    </td>
                  </tr>
                  {expandedRow === r.id && (
                    <tr key={`${r.id}-x`} className="bg-gray-50">
                      <td className="border border-gray-200 px-2 py-1 text-gray-300" />
                      <td colSpan={9} className="border border-gray-200 px-2 py-2">
                        <p className="text-[10px] font-bold uppercase text-gray-400 mb-1">Details</p>
                        <textarea defaultValue={r.details || ''} rows={3}
                          onBlur={e => e.target.value !== (r.details || '') && patchCell(r.id, 'details', e.target.value)}
                          className="w-full border border-gray-200 rounded px-2 py-1 text-xs bg-white focus:outline-none" />
                        <p className="text-[10px] font-bold uppercase text-gray-400 mb-1 mt-2">Changelog</p>
                        <textarea defaultValue={r.changelog || ''} rows={3}
                          onBlur={e => e.target.value !== (r.changelog || '') && patchCell(r.id, 'changelog', e.target.value)}
                          className="w-full border border-gray-200 rounded px-2 py-1 text-xs bg-white focus:outline-none" />
                      </td>
                    </tr>
                  )}
                </>
              ))}
            </tbody>
          </table>
        </div>
      ) : grouped.length === 0 ? (
        <p className="text-sm text-gray-400 py-2">
          {needle ? `Nothing in the history matches "${q}".` : 'Queue clear — nothing in progress.'}
        </p>
      ) : grouped.map(g => (
        <div key={g.st} className="mb-2 last:mb-0">
          <p className="text-[10px] font-bold uppercase tracking-wide text-gray-400 mb-1 flex items-center gap-1">
            <span className={`material-symbols-outlined text-sm ${g.st === 'in_progress' ? 'animate-spin' : ''}`}>{STATUS_META[g.st]?.icon}</span>
            {STATUS_META[g.st]?.label} · {g.rows.length}
          </p>
          <div className="divide-y divide-gray-50">
            {g.rows.map(r => (
              <div key={r.id}>
                <button onClick={() => setOpenId(o => o === r.id ? null : r.id)}
                  className="w-full flex items-center gap-2 py-1.5 text-left hover:bg-gray-50 rounded px-1">
                  <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${STATUS_META[r.status]?.cls}`}>#{r.id}</span>
                  <span className="flex-1 min-w-0 text-sm text-gray-800 truncate">{r.title}</span>
                  {r.fulfill_now === 1 && r.status !== 'done' && <span className="text-[9px] font-bold text-[#C77D3F]">NOW</span>}
                  <span className="text-[10px] text-gray-400">{r.source}</span>
                  <span className="text-[10px] text-gray-400 hidden sm:inline">{r.page}</span>
                </button>
                {openId === r.id && (
                  <div className="pl-2 pb-2 pr-1 text-xs">
                    {r.status === 'proposed' && (
                      <button onClick={() => approve(r.id)}
                        className="mb-1.5 h-7 px-3 rounded-lg bg-[#5C7F4F] text-white text-[11px] font-bold">
                        Approve → queue it
                      </button>
                    )}
                    {r.changelog ? (
                      <p className="text-gray-600 whitespace-pre-wrap bg-gray-50 rounded-lg p-2">{r.changelog}</p>
                    ) : (
                      <p className="text-gray-400 italic">No changelog yet — {r.status === 'proposed' ? 'awaiting your approval.' : 'not fulfilled yet.'}</p>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
